import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  QrCodeIcon,
  PlusIcon,
  MinusIcon,
  CheckIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  DevicePhoneMobileIcon,
  BuildingOfficeIcon,
  ClipboardDocumentCheckIcon,
} from '@heroicons/react/24/outline';
import {
  conferenciaApi,
  type NfeConferencia
} from '../api/conferencia';
import { formatDateTime } from '../utils/format';
import { useToast } from '../components/ui/Toast';
import { logError } from '../utils/clientLog';

interface NovoProduto {
  produto: string;
  descricaoProduto: string;
  qtdeConferida: number;
  qtdeEmbalagem: number;
  status: string;
}

const NovaConferenciaPage: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Estados do formulário
  const [empresa, setEmpresa] = useState('001'); // Empresa padrão
  const [observacao, setObservacao] = useState('');
  const [produtos, setProdutos] = useState<NovoProduto[]>([]);

  // Estados para NFe
  const [nfeSelecionada, setNfeSelecionada] = useState<NfeConferencia | null>(null);
  const [danfeInput, setDanfeInput] = useState('');
  const [buscandoNfe, setBuscandoNfe] = useState(false);

  // Estados para scanner
  const [codigoBarras, setCodigoBarras] = useState('');
  const [scannerAtivo, setScannerAtivo] = useState(false);
  const codigoBarrasRef = useRef<HTMLInputElement>(null);

  // Detectar mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto-focus no scanner
  useEffect(() => {
    if (scannerAtivo && codigoBarrasRef.current) {
      codigoBarrasRef.current.focus();
    }
  }, [scannerAtivo]);

  // Buscar NFe por DANFE
  const buscarNfePorDanfe = async () => {
    if (!danfeInput || danfeInput.length !== 44) {
      toast.aviso('DANFE deve ter 44 dígitos');
      return;
    }

    try {
      setBuscandoNfe(true);
      const nfe = await conferenciaApi.buscarNfePorDanfe(danfeInput);
      setNfeSelecionada(nfe);
      
      // Adicionar produtos da NFe automaticamente
      const produtosNfe = nfe.produtos.map(p => ({
        produto: p.codigo || '',
        descricaoProduto: p.descricao || 'Produto não identificado',
        qtdeConferida: 0, // Iniciar com 0 para conferir
        qtdeEmbalagem: p.quantidade || 1,
        status: 'PENDENTE',
      }));
      
      setProdutos(produtosNfe);
    } catch (err) {
      logError('Erro ao buscar NFe:', err);
      toast.erro('NFe não encontrada ou não aprovada');
    } finally {
      setBuscandoNfe(false);
    }
  };

  // Processar código de barras
  const processarCodigoBarras = async () => {
    if (!codigoBarras.trim()) return;

    try {
      const produto = await conferenciaApi.buscarProdutoPorBarras(codigoBarras);
      
      // Verificar se produto já existe
      const produtoExistente = produtos.find(p => p.produto === produto.produtoPrincipal);
      
      if (produtoExistente) {
        // Incrementar quantidade
        setProdutos(prev => prev.map(p => 
          p.produto === produto.produtoPrincipal 
            ? { ...p, qtdeConferida: p.qtdeConferida + 1 }
            : p
        ));
      } else {
        // Adicionar novo produto
        const novoProduto: NovoProduto = {
          produto: produto.produtoPrincipal,
          descricaoProduto: produto.descricao || 'Produto não identificado',
          qtdeConferida: 1,
          qtdeEmbalagem: 1,
          status: 'CONFERIDO',
        };
        setProdutos(prev => [...prev, novoProduto]);
      }

      // Limpar campo e manter foco
      setCodigoBarras('');
      setTimeout(() => {
        codigoBarrasRef.current?.focus();
      }, 100);

    } catch (err) {
      logError('Erro ao buscar produto:', err);
      toast.erro('Produto não encontrado com este código de barras');
      setCodigoBarras('');
    }
  };

  // Atualizar quantidade do produto
  const atualizarQuantidadeProduto = (produtoId: string, novaQtde: number) => {
    setProdutos(prev => prev.map(p =>
      p.produto === produtoId 
        ? { ...p, qtdeConferida: Math.max(0, novaQtde) }
        : p
    ));
  };

  // Remover produto
  const removerProduto = (produtoId: string) => {
    setProdutos(prev => prev.filter(p => p.produto !== produtoId));
  };

  // Salvar conferência
  const salvarConferencia = async () => {
    if (!empresa.trim()) {
      toast.aviso('Empresa é obrigatória');
      return;
    }

    if (produtos.length === 0) {
      toast.aviso('Adicione pelo menos um produto à conferência');
      return;
    }

    try {
      setLoading(true);
      
      const { codigoId } = await conferenciaApi.criarConferencia({
        empresa,
        observacao,
        produtos: produtos.map(p => ({
          produto: p.produto,
          qtdeConferida: p.qtdeConferida,
          qtdeEmbalagem: p.qtdeEmbalagem,
          status: p.status,
        })),
      });

      toast.sucesso('Conferência criada com sucesso!');
      navigate(`/conferencia/${empresa}/${codigoId}`);
    } catch (err) {
      logError('Erro ao criar conferência:', err);
      toast.erro('Erro ao criar conferência');
    } finally {
      setLoading(false);
    }
  };


  const getStatusColor = (status: string) => {
    switch (status) {
      case 'CONFERIDO':
        return 'bg-emerald-100 text-emerald-800';
      case 'PENDENTE':
        return 'bg-amber-100 text-amber-800';
      default:
        return 'bg-zinc-100 text-zinc-800';
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Page header canônico (UX_AUDIT R1) */}
        <button
          onClick={() => navigate('/conferencia')}
          className="mb-3 flex items-center text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
        >
          <ArrowLeftIcon className="h-3.5 w-3.5 mr-1" />
          Voltar para lista
        </button>
        <header className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div className="flex items-center gap-2">
            {isMobile
              ? <DevicePhoneMobileIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" aria-hidden />
              : <ClipboardDocumentCheckIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" aria-hidden />}
            <div>
              <h1 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">Nova Conferência</h1>
              <p className="text-xs text-zinc-500 dark:text-zinc-400">Conferência de Nota Fiscal Eletrônica.</p>
            </div>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={salvarConferencia}
              disabled={loading || produtos.length === 0}
              className="bg-emerald-600 text-white px-6 py-2 rounded-md hover:bg-emerald-700 disabled:opacity-50 flex items-center"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
              ) : (
                <CheckIcon className="h-5 w-5 mr-2" />
              )}
              Salvar Conferência
            </button>
            <button
              onClick={() => navigate('/conferencia')}
              className="bg-zinc-600 text-white px-4 py-2 rounded-md hover:bg-zinc-700 flex items-center"
            >
              <XMarkIcon className="h-5 w-5 mr-2" />
              Cancelar
            </button>
          </div>
        </header>

        <div className={`grid ${isMobile ? 'grid-cols-1 gap-4' : 'grid-cols-1 lg:grid-cols-2 gap-6'}`}>
          {/* Painel Esquerdo - Configurações e Scanner */}
          <div className="space-y-6">
            {/* Configurações Básicas */}
            <div className="bg-white p-6 rounded-lg shadow">
              <h2 className="text-lg font-semibold text-zinc-900 mb-4 flex items-center">
                <BuildingOfficeIcon className="h-5 w-5 mr-2" />
                Configurações
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-2">
                    Empresa *
                  </label>
                  <select
                    value={empresa}
                    onChange={(e) => setEmpresa(e.target.value)}
                    className="w-full border border-zinc-300 rounded-md px-3 py-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="001">001 - Empresa Principal</option>
                    <option value="002">002 - Filial Norte</option>
                    <option value="003">003 - Filial Sul</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Buscar NFe por DANFE */}
            <div className="bg-white p-6 rounded-lg shadow">
              <h2 className="text-lg font-semibold text-zinc-900 mb-4">Vincular NFe</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-2">
                    Número DANFE (44 dígitos)
                  </label>
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={danfeInput}
                      onChange={(e) => setDanfeInput(e.target.value)}
                      placeholder="Digite o código DANFE..."
                      maxLength={44}
                      className="flex-1 border border-zinc-300 rounded-md px-3 py-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                    <button
                      onClick={buscarNfePorDanfe}
                      disabled={buscandoNfe || danfeInput.length !== 44}
                      className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 disabled:opacity-50 flex items-center"
                    >
                      {buscandoNfe ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      ) : (
                        <>
                          <MagnifyingGlassIcon className="h-5 w-5 mr-2" />
                          Buscar
                        </>
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-zinc-500 mt-1">
                    {danfeInput.length}/44 dígitos
                  </p>
                </div>

                {nfeSelecionada && (
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                    <h3 className="font-medium text-blue-900">NFe Vinculada</h3>
                    <div className="mt-2 text-sm text-blue-700">
                      <p>Número: {nfeSelecionada.numeroNfe}</p>
                      <p>Fornecedor: {nfeSelecionada.fornecedor}</p>
                      <p>Data: {formatDateTime(nfeSelecionada.dataEmissao!)}</p>
                      <p>Valor: R$ {nfeSelecionada.valorTotal?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                      <p>Produtos adicionados: {nfeSelecionada.produtos.length}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Scanner de Código de Barras */}
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-zinc-900">Scanner de Código de Barras</h2>
                <button
                  onClick={() => setScannerAtivo(!scannerAtivo)}
                  className={`px-4 py-2 rounded-md flex items-center ${
                    scannerAtivo 
                      ? 'bg-rose-600 text-white hover:bg-rose-700' 
                      : 'bg-emerald-600 text-white hover:bg-emerald-700'
                  }`}
                >
                  <QrCodeIcon className="h-5 w-5 mr-2" />
                  {scannerAtivo ? 'Desativar' : 'Ativar'} Scanner
                </button>
              </div>

              {scannerAtivo && (
                <div className="space-y-4">
                  <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
                    <p className="text-amber-800 text-sm">
                      💡 Scanner ativo! Use o leitor de código de barras ou digite manualmente.
                    </p>
                  </div>
                  <div className="flex space-x-2">
                    <input
                      ref={codigoBarrasRef}
                      type="text"
                      value={codigoBarras}
                      onChange={(e) => setCodigoBarras(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          processarCodigoBarras();
                        }
                      }}
                      placeholder="Escaneie ou digite o código de barras..."
                      className="flex-1 border border-zinc-300 rounded-md px-3 py-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                    <button
                      onClick={processarCodigoBarras}
                      disabled={!codigoBarras.trim()}
                      className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 disabled:opacity-50"
                    >
                      <PlusIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Observações */}
            <div className="bg-white p-6 rounded-lg shadow">
              <h2 className="text-lg font-semibold text-zinc-900 mb-4">Observações</h2>
              <textarea
                value={observacao}
                onChange={(e) => setObservacao(e.target.value)}
                placeholder="Digite observações sobre a conferência..."
                rows={4}
                className="w-full border border-zinc-300 rounded-md px-3 py-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>

          {/* Painel Direito - Produtos */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-lg font-semibold text-zinc-900 mb-4">
              Produtos a Conferir ({produtos.length})
            </h2>

            {produtos.length === 0 ? (
              <div className="text-center py-12">
                <QrCodeIcon className="h-12 w-12 text-zinc-400 mx-auto mb-4" />
                <p className="text-zinc-500 mb-2">
                  Nenhum produto adicionado ainda.
                </p>
                <p className="text-sm text-zinc-400">
                  Vincule uma NFe ou use o scanner de código de barras para adicionar produtos.
                </p>
              </div>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {produtos.map((produto) => (
                  <div
                    key={produto.produto}
                    className="border border-zinc-200 rounded-lg p-4 hover:border-zinc-300 transition-colors"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-zinc-900 truncate">
                          {produto.descricaoProduto}
                        </h3>
                        <p className="text-sm text-zinc-500 mt-1">
                          Código: {produto.produto}
                        </p>
                        <div className="mt-2 flex items-center space-x-4">
                          <div className="flex items-center space-x-2">
                            <span className="text-sm text-zinc-600">Qtde Conferida:</span>
                            <div className="flex items-center space-x-1">
                              <button
                                onClick={() => atualizarQuantidadeProduto(produto.produto, produto.qtdeConferida - 1)}
                                className="w-8 h-8 flex items-center justify-center border border-zinc-300 rounded-md text-zinc-600 hover:bg-zinc-50"
                              >
                                <MinusIcon className="h-4 w-4" />
                              </button>
                              <input
                                type="number"
                                value={produto.qtdeConferida}
                                onChange={(e) => atualizarQuantidadeProduto(produto.produto, parseFloat(e.target.value) || 0)}
                                className="w-16 text-center border border-zinc-300 rounded-md py-1"
                                min="0"
                                step="0.01"
                              />
                              <button
                                onClick={() => atualizarQuantidadeProduto(produto.produto, produto.qtdeConferida + 1)}
                                className="w-8 h-8 flex items-center justify-center border border-zinc-300 rounded-md text-zinc-600 hover:bg-zinc-50"
                              >
                                <PlusIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                          <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(produto.status)}`}>
                            {produto.status}
                          </span>
                        </div>
                        {produto.qtdeEmbalagem !== produto.qtdeConferida && (
                          <p className="text-xs text-amber-600 mt-1">
                            ⚠️ Esperado: {produto.qtdeEmbalagem} | Conferido: {produto.qtdeConferida}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => removerProduto(produto.produto)}
                        className="ml-4 p-2 text-rose-600 hover:bg-rose-50 rounded-md"
                        title="Remover produto"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {produtos.length > 0 && (
              <div className="mt-4 pt-4 border-t border-zinc-200">
                <div className="text-sm text-zinc-600">
                  <p>Total de produtos: {produtos.length}</p>
                  <p>Produtos conferidos: {produtos.filter(p => p.qtdeConferida > 0).length}</p>
                  <p>Produtos pendentes: {produtos.filter(p => p.qtdeConferida === 0).length}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NovaConferenciaPage;